import { useEffect, useState } from "react";
import { InitialPrompt, MobileExplorer, WorldMap } from "components/organisms";
import { OceanScene } from "components/templates";
import { Link } from "react-router-dom";
import countriesData from "utils/countriesData.json";
import styled from "styled-components";
import { useAppLanguage } from "context/AppLanguageContext";
import { learnHref } from "utils/learnPaths";
import {
  otherLanguage,
  switchLanguageAria,
  switchLanguageLabel,
} from "utils/functions";
import { AvailableLanguages } from "utils/types";

const TABLET_MIN_WIDTH_QUERY = "(min-width: 768px)";

// A horizontal row, not a stacked column: WorldMap reserves a guaranteed
// map-free strip along the bottom of the screen (see CustomSVG's height in
// WorldMap.tsx) sized for one row of controls, not three stacked ones —
// stacking them upright is what let this reach up far enough to sit on top
// of New Zealand on some window sizes.
const NavStack = styled.div`
  position: fixed;
  right: 18px;
  bottom: 14px;
  z-index: 5;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

const floatingButtonStyles = `
  cursor: pointer;
  border: 1px solid rgba(245, 226, 200, 0.35);
  color: #f5e2c8;
  background: rgba(27, 38, 79, 0.24);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 10px 12px;
  border-radius: 999px;
  letter-spacing: 0.2px;
  font-family: "Finger Paint", cursive;
  text-decoration: none;
  transition:
    transform 120ms ease,
    background 120ms ease,
    border 120ms ease;

  &:hover {
    transform: translateY(-1px);
    background: rgba(27, 38, 79, 0.34);
    border-color: rgba(245, 226, 200, 0.6);
  }

  &:active {
    transform: translateY(0px);
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

const FloatingButton = styled.button`
  ${floatingButtonStyles}
`;

const FloatingLink = styled(Link)`
  ${floatingButtonStyles}
`;

const getIsTabletOrBigger = () =>
  typeof window === "undefined" ||
  window.matchMedia(TABLET_MIN_WIDTH_QUERY).matches;

const useIsTabletOrBigger = () => {
  const [isTabletOrBigger, setIsTabletOrBigger] =
    useState(getIsTabletOrBigger);

  useEffect(() => {
    const mediaQuery = window.matchMedia(TABLET_MIN_WIDTH_QUERY);
    const updateScreenSize = () => setIsTabletOrBigger(mediaQuery.matches);

    updateScreenSize();
    mediaQuery.addEventListener("change", updateScreenSize);

    return () => mediaQuery.removeEventListener("change", updateScreenSize);
  }, []);

  return isTabletOrBigger;
};

export const Home = () => {
  const { language, setLanguage } = useAppLanguage();
  const isTabletOrBigger = useIsTabletOrBigger();
  const start = language !== undefined;
  const [matchingPath, setMatchingPath] = useState("");

  const showInitialPrompt = !start;

  // Phones used to get a modal telling them to come back on a bigger screen —
  // the whole app, search included, was unreachable. They now get a layout
  // built for the size: search, continent chips, a continent-cropped map and
  // a browsable country list.
  if (start && !isTabletOrBigger && language) {
    return (
      <OceanScene showFinder={false} setMatchingPath={setMatchingPath}>
        <MobileExplorer
          data={countriesData}
          language={language}
          onSwitchLanguage={() => setLanguage(otherLanguage(language))}
        />
      </OceanScene>
    );
  }

  return (
    <OceanScene
      showFinder={start && isTabletOrBigger}
      language={language}
      setMatchingPath={setMatchingPath}
    >
      <InitialPrompt
        handleLanguagePress={setLanguage}
        hidden={!showInitialPrompt}
      />

      {start && isTabletOrBigger && (
        <NavStack>
          <FloatingLink to="/autotest">
            {language === AvailableLanguages.ES ? "Autotest" : "Self-quiz"}
          </FloatingLink>
          <FloatingLink to={learnHref(language === AvailableLanguages.ES)}>
            {language === AvailableLanguages.ES ? "Aprender" : "Learn"}
          </FloatingLink>
          {/* Names the language you'd be switching *to*, and switches on one
              click. Re-opening the two-option picker would be a second tap to
              answer a question the label already answered. */}
          <FloatingButton
            onClick={() => language && setLanguage(otherLanguage(language))}
            aria-label={language ? switchLanguageAria(language) : undefined}
          >
            <span aria-hidden="true">🌐</span>{" "}
            {language ? switchLanguageLabel(language) : ""}
          </FloatingButton>
        </NavStack>
      )}

      <WorldMap
        data={countriesData}
        language={language}
        focusedCountry={matchingPath}
      />
    </OceanScene>
  );
};
